if (!window.AnnouncementBar) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.config = {
        autorotate: this.dataset.autorotate === 'true',
        autorotateSpeed: parseInt(this.dataset.autorotateSpeed) * 1000,
        moveTime: parseFloat(this.dataset.speed),
        space: 100,
      };

      if (theme.config.mqlSmall) {
        this.initOnMobile();
      } else {
        this.initOnDesktop();
      }

      document.addEventListener('matchSmall', () => {
        this.unload();
        this.initOnMobile();
      });

      document.addEventListener('unmatchSmall', () => {
        this.unload();
        this.initOnDesktop();
      });
    }

    initOnDesktop() {
      switch (this.dataset.layout) {
        case 'carousel':
          this.initCarousel();
          break;
        case 'marquee':
          this.initMarquee();
          break;
        default:
          this.initMarqueeOptimized();
      }
    }

    initOnMobile() {
      switch (this.dataset.mobileLayout) {
        case 'carousel':
          this.initCarousel();
          break;
        case 'marquee':
          this.initMarquee();
          break;
      }
    }

    unload() {
      if (this.flickity && typeof this.flickity.destroy === 'function') {
        this.flickity.destroy();
        this.removeNextPrevListeners();
        this.flickity = null;
      }

      if (this.classList.contains('marquee')) {
        this.classList.remove('marquee');
        this.querySelectorAll('.announcement-slider[aria-hidden]').forEach(slider => {
          slider.remove();
        });
        this.isAnimated = false;
      }

      if (this.observer) {
        this.observer.unobserve(this);
        this.observer.disconnect();
        this.observer = null;
      }
    }

    initCarousel() {
      if (this.dataset.blockCount == 1) return;

      const slider = this.querySelector('.announcement-slider');

      setTimeout(() => {
        this.flickity = new Flickity(slider, {
          accessibility: false,
          rightToLeft: theme.config.rtl,
          prevNextButtons: false,
          pageDots: false,
          wrapAround: true,
          setGallerySize: false,
          autoPlay: this.config.autorotate ? this.config.autorotateSpeed : false,
        });
      });

      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');

      this.onNextPrevClick = this.handleNextPrevClick.bind(this);
      this.prevButton?.addEventListener('click', this.onNextPrevClick);
      this.nextButton?.addEventListener('click', this.onNextPrevClick);
    }

    removeNextPrevListeners() {
      this.prevButton?.removeEventListener('click', this.onNextPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextPrevClick);
    }

    handleNextPrevClick(event) {
      event.preventDefault();

      if (!this.flickity) return;

      if (event.currentTarget.name === 'next') {
        this.flickity.next();
      } else {
        this.flickity.previous();
      }
    }

    initMarqueeOptimized() {
      setTimeout(() => this.marqueeAnimation());

      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!this.isAnimated) return;
          this.classList.toggle('marquee--paused', !entry.isIntersecting);
        });
      }, { rootMargin: '0px 0px 50px 0px' });

      this.observer.observe(this);

      this.onResize = this.marqueeAnimation.bind(this);
      window.addEventListener('resize', this.onResize);
    }

    marqueeAnimation() {
      const slider = this.querySelector('.announcement-slider');
      if (!slider) return;

      if (slider.scrollWidth > this.clientWidth) {
        if (!this.isAnimated) {
          for (let i = 0; i < 2; i++) {
            const clone = slider.cloneNode(true);
            clone.setAttribute('aria-hidden', true);
            this.appendChild(clone);
          }

          this.classList.add('marquee');
          this.style.setProperty(
            '--duration',
            `${(slider.scrollWidth / this.config.space) * this.config.moveTime}s`
          );
          this.isAnimated = true;
        }
      } else if (this.isAnimated) {
        this.querySelectorAll('.announcement-slider[aria-hidden]').forEach(el => el.remove());
        this.classList.remove('marquee');
        this.isAnimated = false;
      }
    }

    initMarquee() {
      setTimeout(() => {
        const slider = this.querySelector('.announcement-slider');
        if (!slider) return;

        for (let i = 0; i < 10; i++) {
          const clone = slider.cloneNode(true);
          clone.setAttribute('aria-hidden', true);
          this.appendChild(clone);
        }

        this.classList.add('marquee');
        this.style.setProperty(
          '--duration',
          `${(slider.clientWidth / this.config.space) * this.config.moveTime}s`
        );
      });

      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          this.classList.toggle('marquee--paused', !entry.isIntersecting);
        });
      }, { rootMargin: '0px 0px 50px 0px' });

      this.observer.observe(this);
    }
  }

  window.AnnouncementBar = AnnouncementBar;
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', window.AnnouncementBar);
}
